import * as React from 'react'

import {
  CopyIcon,
  MoreHorizontalIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  itemId: string
  itemName: string
  onRename: (id: string) => void
  onDelete: (id: string) => void
  onCopy?: (id: string) => void
  /** False guards the last remaining globe — `selectDeletableGlobe` throws on `list.length <= 1`. */
  canDelete?: boolean
  className?: string
}

export const MapViewsSidebarRowMenu: React.FC<IProps> = ({
  itemId,
  itemName,
  onRename,
  onDelete,
  onCopy,
  canDelete = true,
  className,
}) => {
  // Menu items only stop click (not pointerdown) — base-ui's Menu.Item tracks
  // activation on pointerdown, and stopping it silently kills the click.
  const stopAll = (e: React.SyntheticEvent): void => {
    e.stopPropagation()
  }
  const handleRename = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onRename(itemId)
  }
  const handleCopy = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onCopy?.(itemId)
  }
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onDelete(itemId)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            data-slot="map-views-sidebar-row-menu-trigger"
            aria-label={Localization.translator.openActionsForX(itemName)}
            className={className}
            onClick={stopAll}
            onPointerDown={stopAll}
          >
            <MoreHorizontalIcon aria-hidden="true" />
          </Button>
        }
      />
      <DropdownMenuContent align="end" sideOffset={4}>
        <DropdownMenuItem onClick={handleRename}>
          <PencilIcon aria-hidden="true" />
          {Localization.translator.rename}
        </DropdownMenuItem>
        {onCopy && (
          <DropdownMenuItem onClick={handleCopy}>
            <CopyIcon aria-hidden="true" />
            {Localization.translator.duplicate}
          </DropdownMenuItem>
        )}
        {canDelete && (
          <DropdownMenuItem variant="destructive" onClick={handleDelete}>
            <Trash2Icon aria-hidden="true" />
            {Localization.translator.delete}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
